import styled from '@emotion/styled';
import { motion } from 'framer-motion';

import type { CobrainerTheme } from '~/ui/theme/cobrainer-theme';

type SpacingToken = keyof CobrainerTheme['spacing'];

export const Backdrop = styled(motion.div)`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
`;

export const ContentContainer = styled(motion.div)`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: ${({ theme }) => theme.spacing['4xl']};
`;

export const ContentSections = styled.div`
    background-color: ${({ theme }) => theme.colors.white[200]};
    box-sizing: border-box;
    display: grid;
    grid-template-columns: minmax(0px, 1fr) minmax(0px, 1fr);
    gap: ${({ theme }) => theme.spacing.xl};
    align-items: flex-start;
    justify-content: flex-start;
    position: relative;
    width: 100%;
    max-width: 100%;
    margin-top: ${({ theme }) => theme.spacing.xxl};
`;

export const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing['3xl']};
`;

export const SectionHeader = styled.div`
    box-sizing: border-box;
    font-size: ${({ theme }) => theme.typography.body.M.medium.fontSize};
    font-weight: ${({ theme }) => theme.typography.body.M.medium.fontWeight};
    line-height: ${({ theme }) => theme.typography.body.M.medium.lineHeight};
    display: flex;
    flex-direction: row;
    margin-bottom: ${({ theme }) => theme.spacing.l};
    gap: ${({ theme }) => theme.spacing.s};
    align-items: center;
    justify-content: flex-start;
    padding: 0;
    position: relative;
    flex-shrink: 0;
`;

export const SectionContent = styled.div`
    box-sizing: border-box;
    display: block;
    text-align: left;
    width: 100%;
    white-space: break-spaces;
    word-wrap: break-word;
    overflow-wrap: break-word;
    & > p {
        margin: 0;
    }
    & > ul {
        margin: 0;
        padding-left: 0;
    }

    & ul, & ol, & li {
        white-space: normal;
    }

    mark[data-comment-id] {
        background-color: var(--color-orange-100);
        color: inherit;
        border-radius: ${({ theme }) => theme.borderRadius.s};
        padding: 1px 0;
        cursor: pointer;
        transition: background-color 200ms ease;
        -webkit-box-decoration-break: clone;
        box-decoration-break: clone;
    }
    [data-comment-block-id] {
        border-radius: ${({ theme }) => theme.borderRadius.s};
        cursor: pointer;
        transition: background-color 200ms ease;
    }
    [data-comment-block-id]:hover,
    [data-comment-block-id]:has(mark[data-comment-id].is-active),
    [data-comment-block-id]:has(mark[data-comment-id].is-pending) {
        background-color: var(--color-orange-100);
    }
    mark[data-comment-id] mark[data-comment-id] {
        background-color: var(--color-orange-200);
    }
    mark[data-comment-id]:hover,
    mark[data-comment-id].is-active,
    mark[data-comment-id].is-pending {
        background-color: var(--color-orange-300);
    }
    mark[data-comment-id]:hover mark[data-comment-id],
    mark[data-comment-id].is-active mark[data-comment-id],
    mark[data-comment-id].is-pending mark[data-comment-id],
    mark[data-comment-id] mark[data-comment-id]:hover,
    mark[data-comment-id] mark[data-comment-id].is-active,
    mark[data-comment-id] mark[data-comment-id].is-pending {
        background-color: var(--color-orange-300);
    }
`;

export const Wrapper = styled.div`
    display: flex;
    width: 100%;
    min-width: 0;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing['3xl']};
`;

export const StyledStickyHeader = styled.div<{ $paddingBottomToken?: SpacingToken }>`
    position: sticky;
    top: 0;
    padding-top: ${({ theme }) => theme.spacing.xxl};
    z-index: 1;
    background-color: ${({ theme }) => theme.colors.white[200]};
    padding-bottom: ${({ theme, $paddingBottomToken }) => theme.spacing[$paddingBottomToken ?? 'xxl']};
    padding-left: ${({ theme }) => `${theme.spacing.xxl}`};
    margin-left: ${({ theme }) => `-${theme.spacing.xxl}`};
    padding-right: ${({ theme }) => `${theme.spacing.xxl}`};
    margin-right: ${({ theme }) => `-${theme.spacing.xxl}`};
`;

export const FlexRowContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing.xl};
`;

// The old world is wrapping the page with a "white dummy card",
// in the new world we have to remove the background and padding/margins.
export const GrayWrapper = styled.div`
    background-color: ${({ theme }) => theme.colors.gray[200]};
    border-radius: 0;
    height: 100%;
`;

export const GrayWrapperInner = styled.div`
    padding-left: ${({ theme }) => theme.spacing.m};
`;
